// Market commission detail (SM-310).
//
// Answers the question an operator actually arrives with: "why is this
// market paying THAT rate, to THAT admin?" The answer is always the
// same shape — the terms were snapshotted when the market first
// published and have not moved since — so the page leads with the
// locked snapshot rather than with totals.
//
// The distinction that matters here: the effective configuration is
// NOT the current configuration. An event override changed after
// publish does not touch a locked market, and showing today's config
// would misreport every market that has one.
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { CommissionMetrics } from "@/components/treasury/CommissionMetrics";
import { cn } from "@/lib/cn";
import {
  ChargeRate,
  CommissionRateTriple,
} from "@/components/commission/CommissionRateTriple";
import {
  commissionConfigKeys,
  commissionKeys,
  fetchCommissionCharges,
  fetchCommissionSummary,
  fetchMarketCommission,
  fetchRule,
  keys,
  lockedRates,
  type CommissionFilters,
} from "@/lib/queries";
import { adminName, useAdminMap } from "@/hooks/useAdminNames";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";
import { parseReportCurrency } from "@/components/currency/ReportCurrencySelect";
import { CurrencyBadge } from "@/components/ui/CurrencyAmount";
import type { CurrencyCode } from "@/lib/currency";

/**
 * The currency travels with the drill-down (SM-496) — same contract as
 * the event page: a row read in GREEN opens a page of GREEN.
 *
 * It matters twice here. The strip is an AGGREGATE, which settlement
 * pins to GOLD when nobody names a currency; the charge list is a ROW
 * read, which settlement deliberately leaves UNSCOPED so its rows can
 * carry their own labels. Unscoped, this page would have shown a GOLD
 * total over a mixed-currency list of charges — two answers to one
 * question. Passing the currency puts both on the same book.
 */
export const Route = createFileRoute(
  "/_app/treasury/revenue/markets/$marketId",
)({
  validateSearch: (raw: Record<string, unknown>): { currency: CurrencyCode } => ({
    currency: parseReportCurrency(raw.currency),
  }),
  component: MarketCommissionDetail,
});

const TH = "px-3 py-3 text-2xs font-medium uppercase tracking-wide text-muted";

function MarketCommissionDetail() {
  const { marketId } = Route.useParams();
  const { currency } = useSearch({
    from: "/_app/treasury/revenue/markets/$marketId",
  });
  const currencyName = useCurrencyName(currency);
  const filters: CommissionFilters = useMemo(
    () => ({ market_id: marketId, currency }),
    [marketId, currency],
  );

  const summaryQ = useQuery({
    queryKey: commissionKeys.summary(filters),
    queryFn: () => fetchCommissionSummary(filters),
    refetchInterval: 30_000,
  });
  const admins = useAdminMap();
  // Name the market rather than its ULID (SM-344). A redacted viewer
  // never gets a link here — their group keys are opaque digests — and
  // if one arrives by hand the fetch simply fails and the id stands.
  const marketQ = useQuery({
    queryKey: keys.ruleDetail(marketId),
    queryFn: () => fetchRule(marketId),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const chargesQ = useQuery({
    queryKey: commissionKeys.charges(filters, 0),
    queryFn: () => fetchCommissionCharges(filters, { limit: 100 }),
    refetchInterval: 30_000,
  });
  // The market's LOCKED terms, read from the configuration rather than
  // sampled off a charge.
  //
  // Sampling was correct while a market had one rate; since SM-387 it
  // has three, and the newest charge is whichever fee type happened to
  // land last. Reading the locked config is the only way this page can
  // state all three without implying that the one it sampled is "the"
  // rate. Charges keep their own per-row rate below, which is the rate
  // that actually priced them.
  const lockedQ = useQuery({
    queryKey: commissionConfigKeys.market(marketId),
    queryFn: () => fetchMarketCommission(marketId),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const charges = chargesQ.data?.charges ?? [];
  // The recipient and source ARE identical on every charge (they locked
  // together), so the newest row remains a faithful sample of those.
  const snapshot = charges[0];
  const locked = lockedQ.data?.locked ? lockedQ.data : null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/treasury/revenue/commissions"
          search={{ currency }}
          className="text-xs text-accent hover:underline"
        >
          ← Commissions
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">
          {marketQ.data?.name ?? "Market commission"}
        </h1>
        <p className="text-xs text-muted">
          {marketQ.data?.name ? "Market commission · " : null}
          <span className="font-mono">{marketId}</span>
        </p>
      </div>

      {summaryQ.isError ? (
        <LoadFailed
          what={`this market's ${currencyName} commission`}
          error={summaryQ.error}
          onRetry={() => void summaryQ.refetch()}
        />
      ) : (
        <CommissionMetrics
          totals={summaryQ.data}
          loading={summaryQ.isLoading}
          currency={currency}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Locked terms</CardTitle>
        </CardHeader>
        <CardBody>
          {chargesQ.isLoading ? (
            <Skeleton className="h-20" />
          ) : !snapshot ? (
            <EmptyState
              title="No fee activity yet"
              description={`No ${currencyName} fee-bearing fill has landed on this market yet. Terms lock when the market first publishes and appear here once one has.`}
            />
          ) : (
            <dl className="grid gap-4 sm:grid-cols-3">
              <Field label="Recipient">
                <span
                  className="text-xs"
                  title={snapshot.commission_recipient_admin_id}
                >
                  {adminName(admins, snapshot.commission_recipient_admin_id)}
                </span>
              </Field>
              <Field
                label="Rates"
                hint="Each collected fee is commissioned at the rate for its own type."
              >
                {locked ? (
                  <CommissionRateTriple
                    rates={lockedRates(locked)}
                    className="text-left"
                  />
                ) : (
                  // No locked config to read (a market published before
                  // the commission regime, or a configuration read that
                  // failed). Fall back to the rate the sampled charge
                  // was priced at, LABELLED with the fee type it came
                  // from — an unlabelled number here would be the
                  // misleading single rate this page must not show.
                  <ChargeRate
                    bps={snapshot.commission_bps}
                    feeRole={snapshot.fee_role}
                  />
                )}
              </Field>
              <Field
                label="Rate source"
                hint={sourceExplanation(snapshot.commission_source)}
              >
                <Badge tone="default">{snapshot.commission_source || "—"}</Badge>
              </Field>
            </dl>
          )}
          <p className="mt-4 max-w-[75ch] text-2xs text-muted">
            These terms were snapshotted when the market first published and
            cannot change. Editing the admin default or the event override
            today affects only markets published afterwards — which is why
            this page shows the locked snapshot rather than the current
            configuration.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Fee charges
            {chargesQ.data ? (
              <span className="ml-2 text-sm font-normal text-muted">
                {chargesQ.data.total.toLocaleString()}
              </span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {chargesQ.isLoading ? (
            <div className="space-y-2 p-5">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : charges.length === 0 ? (
            <EmptyState
              title="No fee charges"
              description={`Nothing has traded with a ${currencyName} fee on this market.`}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={cn(TH, "text-left")}>Charged</th>
                    <th className={cn(TH, "text-left")}>Role</th>
                    <th className={cn(TH, "text-left")}>Currency</th>
                    <th className={cn(TH, "text-right")}>Rate</th>
                    <th className={cn(TH, "text-right")}>Fee</th>
                    <th className={cn(TH, "text-right")}>Commission</th>
                    <th className={cn(TH, "text-left")}>State</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((c) => (
                    <tr
                      key={c.charge_id}
                      className="border-b border-border/60 last:border-0"
                    >
                      <td className="px-3 py-2 font-mono text-2xs text-muted">
                        {new Date(c.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-2xs text-t-2">
                        {c.fee_role}
                      </td>
                      {/* Per ROW, not per page. The list is scoped to one
                          currency, so every chip here reads the same —
                          which is the point: a charge is a record of what
                          was collected, and the record states its own
                          unit rather than borrowing the page's. */}
                      <td className="px-3 py-2">
                        <CurrencyBadge currency={c.currency} />
                      </td>
                      {/* The rate this charge was PRICED at, which since
                          SM-387 differs by fee type on the same market.
                          Reading it off the row rather than off the
                          market is what makes the column trustworthy. */}
                      <td className="px-3 py-2 text-right text-2xs">
                        <ChargeRate bps={c.commission_bps} />
                      </td>
                      {/* Each figure wears its OWN field's currency. They
                          are equal under policy v1 — a commission is a
                          slice of a fee already collected, so it is owed
                          in that fee's currency — but the wire states
                          both, and a renderer that assumed the pair would
                          be the last place a divergence showed up. */}
                      <td className="px-3 py-2 text-right">
                        <CurrencyAmount
                          currency={c.currency}
                          value={c.fee_amount}
                          tone="muted"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <CurrencyAmount
                          currency={c.commission_currency ?? c.currency}
                          value={c.commission_amount}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <StateBadge status={c.commission_status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// The precedence chain, spelled out where an operator meets it. "Why
// 20%?" is the question this page exists to answer, and the source code
// alone ("EVENT") does not answer it.
function sourceExplanation(source?: string): string {
  switch (source) {
    case "MARKET":
      return "a rate set on this market specifically";
    case "EVENT":
      return "inherited from the parent event";
    case "ADMIN_DEFAULT":
      return "the recipient admin's default rate";
    case "NONE":
      return "no rate configured anywhere in the chain";
    default:
      return "";
  }
}

function StateBadge({ status }: { status: string }) {
  if (status === "COMMITTED") return <Badge tone="success">committed</Badge>;
  if (status === "PENDING") return <Badge tone="warning">pending</Badge>;
  if (status === "FAILED") return <Badge tone="danger">failed</Badge>;
  // SM-562: priced, but the fee was never collected — nothing owed.
  if (status === "VOID") return <Badge tone="default">voided</Badge>;
  return <Badge tone="default">none</Badge>;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-2xs font-medium uppercase tracking-wide text-muted">
        {label}
      </dt>
      <dd className="mt-1">{children}</dd>
      {hint ? <dd className="mt-0.5 text-2xs text-muted">{hint}</dd> : null}
    </div>
  );
}
